import axios from 'axios';
import jwtDecode from 'jwt-decode';
import urlJoin from 'proper-url-join';
import { BehaviorSubject } from 'rxjs';

import AuthException from '../exceptions/AuthException';
import InactivityException from '../exceptions/InactivityException';
import LoginException from '../exceptions/LoginException';
import ValidationException from '../exceptions/ValidationException';

const baseUrl = process.env.REACT_APP_BASE_URL;

export default class BaseService {
  async handleError(response) {
    if (!response.ok) {
      const body = await response.json();
      if (response.status === 401) {
        if (response.url.indexOf('login') !== -1) {
          throw new LoginException(body);
        }
        throw new AuthException(body.message);
      }

      if (response.status === 400) {
        if (body.validationError) {
          throw new ValidationException(body.errors);
        }
      }

      throw body;
    }

    // return null if it is an empty response
    return response
      .clone()
      .json()
      .catch(() => null);
  }

  async handleAxiosError(response) {
    const body = response.data;
    if (response.status === 401) {
      if (response.config.url.indexOf('login') !== -1) {
        throw new LoginException(body);
      }
      throw new AuthException(body.message);
    }

    if (response.status === 400) {
      if (body.validationError) {
        throw new ValidationException(body.errors);
      }
    }

    throw body;
  }

  delete(path, body) {
    const payload = body ? JSON.stringify(body) : null;
    const headers = this.getHeaders();
    return fetch(urlJoin(baseUrl, path), {
      body: payload,
      headers,
      method: 'DELETE',
    }).then(this.handleError);
  }

  get(path, token) {
    const headers = this.getHeaders(token);
    return fetch(urlJoin(baseUrl, path), {
      headers,
    }).then(this.handleError);
  }

  getAccessToken() {
    const tokenFromStorage = localStorage.getItem('accessToken');
    if (tokenFromStorage) return tokenFromStorage;

    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromUrl = urlParams.get('token');

    if (tokenFromUrl) {
      localStorage.setItem('accessToken', tokenFromUrl);
      this.storeLastUpTime();
      return tokenFromUrl;
    }

    return null;
  }

  getWorkspaceId() {
    return localStorage.getItem('workspaceId');
  }

  getLastUpTime() {
    const value = localStorage.getItem('lastUpTime');
    if (!value) return null;
    return new Date(value);
  }

  clearAppLocalStorage() {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('lastUpTime');
  }

  checkUpTime() {
    const accessToken = this.getAccessToken();
    if (!accessToken) return false;
    const now = new Date();

    try {
      const decoded = jwtDecode(accessToken);
      const { exp } = decoded;
      const expDate = new Date(exp * 1000);
      if (now.getTime() > expDate.getTime()) {
        return false;
      }
    } catch (error) {
      return false;
    }

    if (process.env.NODE_ENV === 'development') return true;

    const lastUpTime = this.getLastUpTime();
    if (!lastUpTime) {
      this.clearAppLocalStorage();
      throw new InactivityException('Logged out due to inactivity');
    }

    const diff = now.getTime() - lastUpTime.getTime();
    if (diff > 900000) {
      this.clearAppLocalStorage();
      throw new InactivityException('Logged out due to inactivity');
    }

    this.storeLastUpTime();
    return true;
  }

  storeLastUpTime() {
    localStorage.setItem('lastUpTime', new Date().toISOString());
  }

  getHeaders(token) {
    if (!token) {
      this.checkUpTime();
    }

    const urlToken = new URLSearchParams(window.location.search).get('token');

    const accessToken = token || this.getAccessToken() || urlToken;
    const defaultDomain = process.env.REACT_APP_DOMAIN_POSTFIX;
    const subdomain = window.location.host === defaultDomain ? '' : window.location.host.split('.')[0];
    return {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
      'sub-domain': subdomain,
      'workspace-id': this.getWorkspaceId() || '1',
    };
  }

  patch(path, body) {
    const headers = this.getHeaders();
    return fetch(urlJoin(baseUrl, path), {
      body: JSON.stringify(body),
      headers,
      method: 'PATCH',
    }).then(this.handleError);
  }

  post(path, body) {
    const headers = this.getHeaders();
    return fetch(urlJoin(baseUrl, path), {
      body: JSON.stringify(body),
      headers,
      method: 'POST',
    }).then(this.handleError);
  }

  put(path, body) {
    const headers = this.getHeaders();
    return fetch(urlJoin(baseUrl, path), {
      body: JSON.stringify(body),
      headers,
      method: 'PUT',
    }).then(this.handleError);
  }

  sendFormData(verb, path, body, progress$ = new BehaviorSubject(0)) {
    const headers = this.getHeaders();
    delete headers['Content-Type'];

    return axios({
      data: body,
      headers,
      method: verb,
      onUploadProgress: (progressEvent) => {
        const percentage = Math.min(100, Math.max(0, (progressEvent.loaded * 100) / progressEvent.total));
        progress$.next(Math.ceil(percentage));
      },
      url: urlJoin(baseUrl, path),
    })
      .then((response) => response?.data)
      .catch((error) => this.handleAxiosError(error.response));
  }

  payloadToFormData(payload, keysToSkip = []) {
    const formData = new FormData();

    Object.keys(payload).forEach((key) => {
      if (keysToSkip.includes(key)) return;
      if (payload[key] !== null && payload[key] !== undefined) {
        formData.append(key, payload[key]);
      }
    });

    return formData;
  }

  addObjectToFormData(formData, object = {}, attributeKey = '') {
    const entries = Object.entries(object);

    entries.forEach(([key, data]) => {
      if (data !== null && data !== undefined) {
        formData.append(`${attributeKey}[${key}]`, data);
      }
    });

    return formData;
  }

  getQueryString(criteria) {
    const queryItems = [];

    if (criteria) {
      Object.keys(criteria).forEach((key) => {
        if (criteria[key] !== null && criteria[key] !== undefined) {
          queryItems.push(`${key}=${criteria[key]}`);
        }
      });
    }

    return queryItems.length ? `?${queryItems.join('&')}` : '';
  }
}
